import { FC, useEffect, useState } from 'react';
import { Button, ButtonLink, ButtonStyles, Popup, RadioGroup, Upload } from '@silverfort/sf-components';
import { useUsers } from '../context/UserManagementContext';

export type UploadCsvType = 'user' | 'token2';

const OPTIONS = [
    //TODO: change tooltip info to actual info
    {
        title: 'Upload user CSV',
        value: 'user',
        tooltipInfo:
            'Use the export button to download the users table, modify it as needed, and upload it here to apply the changes'
    },
    { title: 'Upload Token2 CSV', value: 'token2' }
];

interface Props {
    open: boolean;
    onClose: VoidFunction;
    onDownloadToken2Template: VoidFunction;
    onUploadCsvFile: (file: FormData, type: UploadCsvType) => void;
}

interface ContentProps {
    fileData: FormData | null;
    onDownloadToken2TemplateClick: VoidFunction;
    onFileSelected: (files: FileList | (File | null)[] | null) => void;
    selectedUploadType: UploadCsvType;
    onSelectedUploadTypeChange: (type: UploadCsvType) => void;
}

interface FooterProps {
    handleConfirm: VoidFunction;
    handleCancel: VoidFunction;
    disabled: boolean;
}

const UpdateUsersAndTokensPopup: FC<Props> = ({ open, onClose, onDownloadToken2Template, onUploadCsvFile }) => {
    const [selectedUploadType, setSelectedUploadType] = useState<UploadCsvType>('user');
    const [fileData, setFileData] = useState<FormData | null>(null);
    const { mutate } = useUsers();

    const onFileSelected = (files) => {
        if (files.length > 0) {
            const file = files.item(0);
            const data = new FormData();
            data.append('file', file);
            setFileData(data);
        } else {
            setFileData(null);
        }
    };

    const handleUploadCsvFile = () => {
        if (fileData) {
            onUploadCsvFile(fileData, selectedUploadType);
            onClose();
            mutate();
        }
    };

    useEffect(() => {
        if (!open) {
            setFileData(null);
        }
    }, [open]);

    return (
        <Popup
            open={open}
            popupClassName="!w-[475px] !h-[375px]"
            contentClassName="!py-8"
            title="Update Users & Tokens"
            ContentComponent={UpdateUsersAndTokensPopupContent}
            ContentComponentProps={{
                fileData,
                onFileSelected,
                selectedUploadType: selectedUploadType,
                onDownloadToken2TemplateClick: onDownloadToken2Template,
                onSelectedUploadTypeChange: setSelectedUploadType
            }}
            FooterComponent={FooterComponent}
            FooterComponentProps={{
                handleConfirm: handleUploadCsvFile,
                handleCancel: onClose,
                disabled: !fileData
            }}
            onApprove={handleUploadCsvFile}
            onClose={onClose}
        />
    );
};

const UpdateUsersAndTokensPopupContent: FC<ContentProps> = ({
    onFileSelected,
    fileData,
    onDownloadToken2TemplateClick,
    selectedUploadType,
    onSelectedUploadTypeChange
}) => {
    return (
        <div className="flex flex-col flex-1 gap-6 font-sf">
            <span className="text-13">Upload the updated CSV template to apply changes to the user table</span>
            <div className="flex flex-row gap-2">
                <RadioGroup
                    selectedOption={selectedUploadType}
                    options={OPTIONS}
                    onChange={(value) => onSelectedUploadTypeChange(value as UploadCsvType)}
                />
                <ButtonLink
                    text="Get template"
                    className="text-13"
                    onClick={onDownloadToken2TemplateClick}
                    disabled={selectedUploadType !== 'token2'}
                />
            </div>
            <Upload
                completed={!!fileData}
                className="w-full items-center align-middle h-full"
                onChange={onFileSelected}
                subtitle="Max file size up to 10 MB"
                onRemove={() => onFileSelected(null)}
                accept=".csv"
            />
        </div>
    );
};

const FooterComponent: FC<FooterProps> = ({ handleConfirm, handleCancel, disabled }) => {
    return (
        <div className="flex flex-row flex-1 justify-end gap-2">
            <Button text="Cancel" buttonStyle={ButtonStyles.TertiarySolid} onClick={handleCancel} />
            <Button text="Upload" onClick={handleConfirm} disabled={disabled} />
        </div>
    );
};

export default UpdateUsersAndTokensPopup;
